import React, { useState } from "react";
import client from "../lib/api";
import { DisqualifyDialog } from "./DealControls";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MoreVertical, Trophy, Ban, RotateCcw, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// One-tap rank / disqualify from a property card. Always visible on touch screens, hover-revealed on desktop.
export const CardQuickActions = ({ property: p, onChanged, onOpen }) => {
  const [disqOpen, setDisqOpen] = useState(false);
  const stop = (e) => e.stopPropagation();
  const isOut = p.disposition === "disqualified";

  const rank = async (n) => {
    try { const r = await client.post(`/properties/${p.id}/rank`, { rank: n }); onChanged(r.data); toast.success(n ? `${p.title} → #${n}` : "Removed from top 5"); }
    catch (e) { toast.error("Couldn't rank", { description: e?.response?.data?.detail }); }
  };
  const patch = async (body) => {
    const r = await client.put(`/properties/${p.id}`, body);
    onChanged(r.data);
  };
  const restore = async () => {
    try { await patch({ disposition: "active" }); toast.success("Back in the running"); } catch (e) { toast.error("Couldn't restore"); }
  };

  return (
    <div onClick={stop} onPointerDown={stop} className="absolute right-3 top-[3.9rem] z-10">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button data-testid={`card-actions-${p.id}`} aria-label="Quick actions"
            className="grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white backdrop-blur transition-opacity hover:bg-brand-red sm:opacity-0 sm:group-hover:opacity-100 sm:data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100">
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="glass-strong w-56 border-white/10 p-1.5" data-testid={`card-actions-menu-${p.id}`} onClick={stop}>
          {!isOut && (
            <>
              <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground"><Trophy className="h-3.5 w-3.5 text-amber-400" /> Top 5 rank</DropdownMenuLabel>
              <div className="grid grid-cols-5 gap-1 px-1 pb-1.5" role="group">
                {[1, 2, 3, 4, 5].map((n) => (
                  <DropdownMenuItem key={n} asChild>
                    <button data-testid={`quick-rank-${n}`} onClick={() => rank(n)}
                      className={`grid h-10 cursor-pointer place-items-center rounded-lg text-sm font-bold focus:outline-none ${p.rank === n ? "bg-amber-400 text-black" : "bg-white/[0.05] text-white hover:bg-amber-400/25"}`}>
                      {p.rank === n ? <Check className="h-4 w-4" /> : `#${n}`}
                    </button>
                  </DropdownMenuItem>
                ))}
              </div>
              {p.rank > 0 && (
                <DropdownMenuItem data-testid="quick-unrank" onClick={() => rank(0)} className="cursor-pointer rounded-lg py-2.5 text-sm text-muted-foreground focus:bg-white/5 focus:text-white">
                  Remove from top 5
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/[0.06]" />
              <DropdownMenuItem data-testid="quick-disqualify" onClick={() => setDisqOpen(true)} className="cursor-pointer rounded-lg py-2.5 text-sm text-white focus:bg-brand-red/15">
                <Ban className="mr-2 h-4 w-4 text-brand-red" /> Disqualify…
              </DropdownMenuItem>
            </>
          )}
          {isOut && (
            <DropdownMenuItem data-testid="quick-restore" onClick={restore} className="cursor-pointer rounded-lg py-2.5 text-sm text-brand-green focus:bg-brand-green/10">
              <RotateCcw className="mr-2 h-4 w-4" /> Restore to active
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className="bg-white/[0.06]" />
          <DropdownMenuItem data-testid="quick-open" onClick={() => onOpen(p.id)} className="cursor-pointer rounded-lg py-2.5 text-sm text-muted-foreground focus:bg-white/5 focus:text-white">
            <ArrowRight className="mr-2 h-4 w-4" /> Open property
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DisqualifyDialog open={disqOpen} onOpenChange={setDisqOpen} onConfirm={patch} title={p.title} />
    </div>
  );
};
